import { useCallback, useRef, useState } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";

// «Spør Claude» i Cmd+K-paletten: samtalen holdes i minnet så lenge appen er
// åpen (hooken bor i CommandPalette, som alltid er montert), slik at Tab med
// tomt felt viser forrige samtale. Etter INACTIVITY_MS uten aktivitet starter
// neste åpning en ny samtale.
//
// Hele samtalen sendes til edge-funksjonen ask_claude ved hver melding, og
// svaret leses som SSE (text/event-stream) fra en ReadableStream.

export type ChatMessage =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string };

// Assistent-meldinger i UI-et kan være avbrutt (Escape under strømming).
export type DisplayMessage = ChatMessage & { aborted?: boolean };

export type AskStatus = "idle" | "streaming" | "error";

type StreamEvent =
  | { type: "status"; text: string }
  | { type: "delta"; text: string }
  | { type: "sources"; refs: string[] }
  | { type: "done" }
  | { type: "error"; message: string };

// Klienten kapper historikken til de siste MAX_HISTORY meldingene før
// sending; edge-funksjonen avviser meldinger over MAX_MESSAGE_CHARS.
export const MAX_HISTORY = 20;

// Posten samtalen gjelder («Spør Claude» på kundekortet, prosjektet eller
// muligheten). Bare type og id sendes; navnet vises i topplinjen.
export type AskTarget = {
  type: "customer" | "project" | "deal";
  id: number;
  name: string;
};

// Hendelsen AskAboutButton sender; CommandPalette åpner chatten med den.
export const ASK_ABOUT_EVENT = "mimin:ask-about";

export const askAbout = (target: AskTarget) =>
  window.dispatchEvent(
    new CustomEvent<AskTarget>(ASK_ABOUT_EVENT, { detail: target }),
  );

export const MAX_MESSAGE_CHARS = 4000;
export const INACTIVITY_MS = 15 * 60 * 1000;

export const ERROR_NETWORK = "Klarte ikke å nå Claude. Prøv igjen.";
export const ERROR_BUSY = "Claude er opptatt, vent litt og prøv igjen.";
const ERROR_AUTH = "Du er ikke lenger innlogget. Last inn siden på nytt.";
export const ERROR_TIMEOUT =
  "Claude brukte for lang tid på å svare. Prøv et mer avgrenset spørsmål.";

export type AskClaude = {
  messages: DisplayMessage[];
  status: AskStatus;
  // Hva Claude henter akkurat nå («Søker i oppgaver …»); null når tekst
  // strømmer eller ingenting skjer.
  statusText: string | null;
  error: string | null;
  // Postene («type:id») verktøyene har returnert i samtalen. Svaret lenker
  // bare til disse; andre mimin://-lenker vises som tekst (PRD US27.2).
  sources: ReadonlySet<string>;
  // Posten samtalen gjelder, eller null for en vanlig samtale.
  context: AskTarget | null;
  send: (text: string) => void;
  retry: () => void;
  abort: () => void;
  reset: () => void;
  // Ny samtale om én post.
  startAbout: (target: AskTarget) => void;
  // Kalles når brukeren går inn i ask-modus: nullstiller samtalen hvis den
  // har stått urørt lenger enn INACTIVITY_MS.
  resumeOrReset: () => void;
};

// Det som sendes: de siste MAX_HISTORY meldingene, uten UI-felt, og med
// lange svar klippet så de holder seg under grensen i edge-funksjonen.
export const toRequestMessages = (messages: DisplayMessage[]): ChatMessage[] =>
  messages.slice(-MAX_HISTORY).map(({ role, content }) => ({
    role,
    content:
      content.length > MAX_MESSAGE_CHARS
        ? `${content.slice(0, MAX_MESSAGE_CHARS - 1)}…`
        : content,
  })) as ChatMessage[];

// Leser SSE-hendelser («data: {...}\n\n») fra en strøm.
export async function* readEvents(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<StreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = chunk
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart())
        .join("\n");
      if (data) yield JSON.parse(data) as StreamEvent;
      boundary = buffer.indexOf("\n\n");
    }
    if (done) return;
  }
}

const errorForResponse = async (response: Response): Promise<string> => {
  if (response.status === 429) return ERROR_BUSY;
  if (response.status === 401) return ERROR_AUTH;
  if (response.status === 504) return ERROR_TIMEOUT;
  // 503 brukes når ANTHROPIC_API_KEY mangler; meldingen er skrevet for
  // brukeren og vises som den er.
  if (response.status === 503) {
    try {
      const body = (await response.json()) as { message?: unknown };
      if (typeof body.message === "string") return body.message;
    } catch {
      // Ikke JSON (f.eks. fra gatewayen); faller til standardmeldingen.
    }
  }
  return ERROR_NETWORK;
};

const functionUrl = () =>
  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask_claude`;

export const useAskClaude = (): AskClaude => {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [status, setStatus] = useState<AskStatus>("idle");
  const [statusText, setStatusText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sources, setSources] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [context, setContext] = useState<AskTarget | null>(null);
  const contextRef = useRef<AskTarget | null>(null);
  const lastActivity = useRef(0);
  const controller = useRef<AbortController | null>(null);
  // Speiler `messages` så send/retry ser siste versjon uten å vente på render.
  const messagesRef = useRef<DisplayMessage[]>([]);

  const update = useCallback((next: DisplayMessage[]) => {
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const touch = useCallback(() => {
    lastActivity.current = Date.now();
  }, []);

  const appendToAnswer = useCallback(
    (text: string) => {
      const current = messagesRef.current;
      const last = current[current.length - 1];
      if (last?.role !== "assistant") return;
      update([
        ...current.slice(0, -1),
        { ...last, content: last.content + text },
      ]);
    },
    [update],
  );

  const run = useCallback(
    async (history: DisplayMessage[]) => {
      const abort = new AbortController();
      controller.current = abort;
      touch();
      setStatus("streaming");
      setStatusText(null);
      setError(null);
      update([...history, { role: "assistant", content: "" }]);

      let failure: string | null = null;
      let finished = false;
      try {
        const { data } = await getSupabaseClient().auth.getSession();
        const token = data.session?.access_token;
        if (!token) throw new Error(ERROR_AUTH);

        const response = await fetch(functionUrl(), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages: toRequestMessages(history),
            context: contextRef.current
              ? { type: contextRef.current.type, id: contextRef.current.id }
              : null,
          }),
          signal: abort.signal,
        });
        if (!response.ok || !response.body) {
          failure = await errorForResponse(response);
        } else {
          for await (const event of readEvents(response.body)) {
            touch();
            if (event.type === "status") {
              setStatusText(event.text);
            } else if (event.type === "sources") {
              // Samles over hele samtalen: et oppfølgingssvar kan lenke til
              // en post fra et tidligere verktøyresultat.
              setSources((prev) => new Set([...prev, ...event.refs]));
            } else if (event.type === "delta") {
              setStatusText(null);
              appendToAnswer(event.text);
            } else if (event.type === "error") {
              failure = event.message;
            } else if (event.type === "done") {
              finished = true;
            }
          }
          // Strømmen ble kuttet uten done eller error (nettverk, timeout).
          if (!finished && !failure) failure = ERROR_NETWORK;
        }
      } catch (e) {
        if (abort.signal.aborted) {
          // Escape: behold teksten som er kommet, og merk svaret som avbrutt.
          const current = messagesRef.current;
          const last = current[current.length - 1];
          if (last?.role === "assistant") {
            update([...current.slice(0, -1), { ...last, aborted: true }]);
          }
        } else {
          failure =
            e instanceof Error && e.message === ERROR_AUTH
              ? ERROR_AUTH
              : ERROR_NETWORK;
        }
      }

      if (controller.current !== abort) return; // nullstilt underveis
      controller.current = null;
      setStatusText(null);
      if (failure) {
        setError(failure);
        setStatus("error");
      } else {
        setStatus("idle");
      }
    },
    [appendToAnswer, touch, update],
  );

  const send = useCallback(
    (text: string) => {
      const content = text.trim().slice(0, MAX_MESSAGE_CHARS);
      if (!content || controller.current) return;
      void run([...messagesRef.current, { role: "user", content }]);
    },
    [run],
  );

  // Sender siste brukermelding på nytt; et halvferdig svar forkastes.
  const retry = useCallback(() => {
    if (controller.current) return;
    const history = [...messagesRef.current];
    while (history.length > 0 && history[history.length - 1].role !== "user") {
      history.pop();
    }
    if (history.length > 0) void run(history);
  }, [run]);

  const abort = useCallback(() => {
    controller.current?.abort();
  }, []);

  const reset = useCallback(() => {
    const running = controller.current;
    controller.current = null;
    running?.abort();
    update([]);
    contextRef.current = null;
    setContext(null);
    setSources(new Set<string>());
    setStatus("idle");
    setStatusText(null);
    setError(null);
    touch();
  }, [touch, update]);

  const startAbout = useCallback(
    (target: AskTarget) => {
      reset();
      contextRef.current = target;
      setContext(target);
    },
    [reset],
  );

  const resumeOrReset = useCallback(() => {
    if (Date.now() - lastActivity.current > INACTIVITY_MS) reset();
  }, [reset]);

  return {
    messages,
    status,
    statusText,
    error,
    sources,
    context,
    send,
    retry,
    abort,
    reset,
    startAbout,
    resumeOrReset,
  };
};
